import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import {
  activity,
  activityCategory,
  userActivityEntry,
} from 'src/app/shared/models/interfaces';
import { UIComponentsModule } from 'src/app/shared/ui-components/ui-components.module';
import { DailyService } from 'src/app/views/logs/daily/daily.service';
import { SnackbarService } from 'src/app/core/services/snackbar/snackbar.service';
import { MatTooltipModule } from '@angular/material/tooltip';
import { isSameDay } from 'src/app/shared/utils/date';
import { Router } from '@angular/router';

@Component({
  selector: 'app-activities',
  imports: [UIComponentsModule, MatTooltipModule],
  templateUrl: './activities.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './activities.component.scss',
})
export class ActivitiesComponent {
  userActivityEntries: userActivityEntry[] = [];
  activityCategories: activityCategory[] = [];
  selectedCategory: activityCategory | null = null;
  selectedActivities: activity[] = [];
  private pendingChanges: userActivityEntry[] = [];

  @Input() selectedDate!: Date;

  // Used to navigate to next daily entry type
  @Output() continueToNext = new EventEmitter<void>();
  
  constructor(
    private dailyService: DailyService,
    private snackbarService: SnackbarService,
    private router: Router
  ) {}

  ngOnInit(): void {
    // Load initial data
    this.loadUserActivityEntries();
    this.loadActivityCategories();
  }

  ngOnDestroy(): void {
    if (this.pendingChanges) {
      this.syncPendingChanges();
    }
  }

  private loadUserActivityEntries() {
    // First load the user activity entries in the daily service
    // and then subscribe to the observable to get real-time updates
    this.dailyService.getUserActivityEntries();
    this.dailyService.userActivityEntries$.subscribe((entries) => {
      this.userActivityEntries = entries;
    });
  }

  private loadActivityCategories() {
    // similar to user activity entries
    this.dailyService.getActivityData();
    this.dailyService.activityCategories$.subscribe((categories) => {
      this.activityCategories = categories;
      this.mapActivityCategoriesToIcons(); // Map icons to categories
      // automatically select the first category and the corresponding activities if none is selected
      if (categories.length > 0 && !this.selectedCategory) {
        this.selectCategory(categories[0]);
        this.selectedActivities = categories[0].activities || [];
      }
    });
  }

  ngOnChanges(): void {
    // React to changes in activityCategories input
    if (
      this.activityCategories &&
      this.activityCategories.length > 0 &&
      !this.selectedCategory
    ) {
      this.selectCategory(this.activityCategories[0]);
      this.selectedActivities = this.activityCategories[0].activities || [];
    }
  }

  private get filteredUserActivityEntries(): userActivityEntry[] {
    return this.userActivityEntries.filter((entry) =>
      isSameDay(entry.occurredAt, this.selectedDate)
    );
  }

  getDurationForActivity(activityId: number): number {
    const entry = this.filteredUserActivityEntries.find(
      (e) => e.activityId === activityId
    );
    return entry ? entry.duration : 0;
  }

  incrementDuration(activityId: number) {
    let entry = this.filteredUserActivityEntries.find(
      (e) => e.activityId === activityId
    );
    if (entry) {
      entry.duration += 1; // Increment by 1 hour (60 minutes)
    } else {
      const newEntry: userActivityEntry = {
        activityId,
        duration: 1, // Start with 1 hour
        occurredAt: this.selectedDate,
        causedEmissions: 0, // Default emissions, updated later
      };
      this.userActivityEntries.push(newEntry);
    }
    this.trackPendingChange(activityId);
  }

  decrementDuration(activityId: number) {
    const entry = this.filteredUserActivityEntries.find(
      (e) => e.activityId === activityId
    );
    if (entry) {
      if (entry.duration > 0) {
        entry.duration --; // Decrement by 1 hour (60 minutes)
      } else {
        // Remove the entry locally if duration reaches 0
        this.userActivityEntries = this.userActivityEntries.filter(
          (e) => e.activityId !== activityId
        );
      }
      this.trackPendingChange(activityId);
    }
  }

  private trackPendingChange(activityId: number) {
    const entry = this.filteredUserActivityEntries.find(
      (e) => e.activityId === activityId
    );
    if (entry) {
      const existingChangeIndex = this.pendingChanges.findIndex(
        (e) => e.activityId === activityId
      );
      if (existingChangeIndex !== -1) {
        this.pendingChanges[existingChangeIndex] = { ...entry };
      } else {
        this.pendingChanges.push({ ...entry });
      }
    }
  }

  selectCategory(category: activityCategory) {
    this.syncPendingChanges(); // Sync changes before switching categories
    this.selectedCategory = category;
    this.selectedActivities =
      category.activities && category.activities.length > 0
        ? category.activities
        : [];
  }

  hasEntries(category: activityCategory): boolean {
    return (
      category.activities?.some((activity) =>
        this.filteredUserActivityEntries.some(
          (entry) => entry.activityId === activity.id && entry.duration > 0
        )
      ) || false
    );
  }

  onContinue() {
    this.syncPendingChanges(); // Sync changes before continuing to the next category
    const currentIndex = this.activityCategories.findIndex(
      (category) => category.id === this.selectedCategory?.id
    );
    if (
      this.selectedCategory &&
      currentIndex < this.activityCategories.length - 1
    ) {
      this.selectCategory(this.activityCategories[currentIndex + 1]);
    } else {
      // Last category in daily logs, redirect to dashboard
      this.router.navigate(['/dashboard']);
    }
  }

  getNextCategoryIcon(category: activityCategory): string {
    const currentCategoryId = category.id;
    const nextIcon = this.activityCategories.find(
      (cat) => cat.id === currentCategoryId + 1
    )?.icon;
    return nextIcon ? nextIcon : 'home';
  }

  private syncPendingChanges() {
    const changesToSync = [...this.pendingChanges];
    this.pendingChanges = [];

    changesToSync.forEach((entry) => {
      if (entry.duration > 0) {
        if (entry.id) {
          // Update existing entry - service will handle state update
          this.dailyService.updateUserActivityEntry(entry).subscribe({
            error: (err) => {
              console.error('Error updating activity entry:', err);
              this.snackbarService.showErrorMessage(
                'Failed to update activity entry. Please try again.'
              );
            },
          });
        } else {
          // Create new entry - service will handle state update
          this.dailyService.createUserActivityEntry(entry).subscribe({
            next: (createdEntry) => {
              const idx = this.userActivityEntries.findIndex(
                (e) => e.activityId === entry.activityId
              );
              if (idx !== -1) {
                this.userActivityEntries[idx] = createdEntry;
              }
            },
            error: (err) => {
              console.error('Error creating activity entry:', err);
              this.snackbarService.showErrorMessage(
                'Failed to create activity entry. Please try again.'
              );
            },
          });
        }
      } else if (entry.id) {
        // Delete entry if duration is 0 - service will handle state update
        this.dailyService.deleteUserActivityEntry(entry.id).subscribe({
          error: (err) => {
            console.error('Error deleting activity entry:', err);
            this.snackbarService.showErrorMessage(
              'Failed to delete activity entry. Please try again.'
            );
          },
        });
      }
    });
  }

  private mapActivityCategoriesToIcons() {
    this.activityCategories = this.activityCategories.map((category) => {
      const categoryKey = category.text.text_en || category.text.text_de;
      return {
        ...category,
        icon: activityIcons[categoryKey] || '',
      };
    });
  }
}

const activityIcons: { [key: string]: string } = {
  'offline activity': 'wifi_off',
  'online activity': 'wifi',
  'digital communication': 'phone',
};
